<template lang="html">
  <component
    @click="handleAction()"
    :is="routeType"
    :href="routeType == 'a' ? link : null"
    :to="routeType == 'RouterLink' ? link : null"
    :class="[
      `button background--${buttonBackground} color--${buttonColor}`,
      { 'has-two-lines': title && subtext }
    ]"
    v-bind="getAttributes"
  >
    <component
      :is="iconLeft"
      v-if="iconLeft && !loading"
      class="button__icon button__icon--left"
    />
    <loader
      v-if="loading"
      class="button__loader"
    />
    <div
      v-else
      class="button__content"
    >
      <span
        class="button__text"
      >
        {{ title }}
      </span>
      <span
        v-if="subtext"
        class="button__subtext"
      >
        {{ subtext }}
      </span>
    </div>
    <component
      :is="iconRight"
      v-if="iconRight && !loading"
      class="button__icon button__icon--right"
      :style="iconStyles"
    />
  </component>
</template>

<script>
import * as components from '@/icons'
import Loader from '@/components/partials/Loader.vue'
import actionHandler from '@/mixins/actionHandler.mixin'

export default  {
  name: 'Button',
  mixins: [actionHandler],
  components: {
    ...components,
    Loader
  },
  props: {
    link: {
      type: [String, Object],
      default: ''
    },
    title: {
      type: String,
      default: ''
    },
    subtext: {
      type: String,
      default: ''
    },
    iconRight: {
      type: String,
      default: ''
    },
    iconLeft: {
      type: String,
      default: ''
    },
    loading: {
      type: Boolean,
      default: false
    },
    buttonBackground: {
      type: String,
      default: 'dark'
    },
    buttonColor: {
      type: String,
      default: 'light'
    },
    external_link: {
      type: Boolean,
      default: false
    },
    iconStyles: {
      type: [Boolean, Object],
      default: false
    }
  },
  computed: {
    routeType() {
      if (!this.link) {
        return 'button'
      }
      const link = typeof this.link === 'object' ? this.link.url : this.link
      const externalLink = new RegExp('^(http://|https://|tel:|mailto:)')
      // Determine type of link
      if (!link.match(externalLink) && !this.anchor) {
        return 'RouterLink'
      }
      return 'a'
    },
    getAttributes () {
      let attrs =  {...this.$attrs}
      if (this.external_link) {
        attrs['target'] = '_blank'
      }


      return attrs
    }
  }
}
</script>

<style lang="scss">
.button {
  position: relative;
  border: none;
  display: inline-block;
  padding: $button-padding;
  width: $button-width;
  text-align: $button-text-align;
  text-decoration: none;
  cursor: pointer;
  span {
    display: block;
  }
  &.has-two-lines {
    padding: $button-padding-has-subtext;
  }
  &[class*="border"] {
    border-width: $button-border-width;
    border-style: solid;
  }
  &--text-only,
  &--icon-only {
    width: auto;
    width: max-content;
  }
  &--icon-only {
    .button__icon {
      position: static;
      transform: none;
    }
  }
  &:focus {
    outline: none;
  }
  &__icon {
    position: absolute;
    fill: currentColor;
    top: 50%;
    width: $default-icon-width;
    height: $default-icon-height;
    transform: translateY(-50%);
    &--left {
      left: $button-icon-padding;
    }
    &--right {
      right: $button-icon-padding;
    }
  }
  &--margin {
    margin: $padding $padding 0;
    width: calc(100% - #{$padding * 2});
  }
}

  .button__content, .button__icon, .button__loader, .button__text, .button__subtext {
    touch-action: none;
  }

</style>

<docs>
  ```jsx
    <Button
      text="Click me"
      subtext="i'm a link"
      buttonColor="light"
      buttonBackground="dark"
      iconRight="arrow-right"
      iconLeft="chevron-left"
    />
    <Button
      text="Click me"
      subtext="i'm a button"
      type="submit"
      buttonColor="dark"
      buttonBackground=""
      iconRight="arrow-right"
      iconLeft="chevron-left"
    />
    <!-- <Button
      text="Click me"
      subtext="i'm a button"
      type="button"
      buttonColor="dark"
      buttonBackground="transparent"
      iconRight="arrow-right"
      iconLeft="chevron-left"
      :loading="true"
    /> -->
  ```
  ```scss
    // Variable defaults:
    $button-width: 100%;
    $button-padding: rem(15 16 17);
    $button-text-align: center;
    $button-icon-padding: rem(21);
    $button-padding-has-subtext: rem(6 16 9);
    $button-border-width: rem(2);
  ```
</docs>
